import React, { useState, useRef, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import RouteIcon from '@mui/icons-material/Route';
import PlaceIcon from '@mui/icons-material/Place';
import EventIcon from '@mui/icons-material/Event';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { frFR, enUS } from '@mui/x-date-pickers/locales';

import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/fr';
import { useTranslation } from 'react-i18next';

import { useAddTrainingMutation, useUpdateTrainingMutation, useGetMyClubsQuery } from '../api/apiSlice';
import { useDecodedToken } from '../app/hooks';
import { SPORT_TYPES } from '../constants/sportTypes';
import { useSportTypeLabel } from '../hooks/useSportTypeLabel';
import { FeedTraining } from '../types/api';
import LocationAutocomplete from './LocationAutocomplete';

interface CreateActivityProps {
    userID: number;
    /** When set, the dialog edits this outing instead of creating a new one. */
    training?: FeedTraining;
    onClose: () => void;
}

interface ActivityFormState {
    TITLE: string;
    ADMIN: number;
    TYPE: string;
    MODE: string;
    DISTANCE: string;
    CLIMB: string;
    START_TIME: string | null;
    DURATION: string | null;
    START_POINT: string;
    CITY: string;
    IMAGE: string | File | null;
}

// Climb doesn't mean anything for a pool swim, and swim distances are
// conventionally tracked in meters rather than kilometers.
const SPORT_FIELD_CONFIG: Record<(typeof SPORT_TYPES)[number], { showClimb: boolean; distanceUnit: string }> = {
    Run: { showClimb: true, distanceUnit: 'km' },
    CO: { showClimb: true, distanceUnit: 'km' },
    'Marche nordique': { showClimb: true, distanceUnit: 'km' },
    Velo: { showClimb: true, distanceUnit: 'km' },
    Natation: { showClimb: false, distanceUnit: 'm' },
    Triathlon: { showClimb: true, distanceUnit: 'km' },
    Autre: { showClimb: true, distanceUnit: 'km' },
};

const SectionLabel = ({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) => (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        {icon}
        <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {children}
        </Typography>
    </Stack>
);

const CreateActivity = ({userID, training, onClose}: CreateActivityProps) => {
    const { t, i18n } = useTranslation();
    const sportTypeLabel = useSportTypeLabel();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const pickersLocaleText = (i18n.language === 'en' ? enUS : frFR).components.MuiLocalizationProvider.defaultProps.localeText;
    const decodedToken = useDecodedToken();
    const { data: myClubs = [] } = useGetMyClubsQuery();
    // Only clubs where the caller can actually post an outing.
    const managerClubs = myClubs.filter((club) => club.ROLE === 'ADMIN' || club.ROLE === 'ANIMATOR');
    const isEdit = Boolean(training);
    const [clubId, setClubId] = useState<number | ''>(training?.CLUB_ID ?? '');

    // Default the picker to the active club when the caller manages it,
    // otherwise the first club they can post to. Skipped when editing -- an
    // outing can't be moved between clubs.
    useEffect(() => {
      if (isEdit || clubId !== '' || managerClubs.length === 0) return;
      const active = managerClubs.find((club) => club.CLUB_ID === decodedToken?.clubId);
      setClubId(active?.CLUB_ID ?? managerClubs[0].CLUB_ID);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myClubs]);

    const trainingImageInputRef = useRef<HTMLInputElement>(null);
    const [startTime, setStartTime] = useState<Dayjs>(training?.START_TIME ? dayjs(training.START_TIME) : dayjs(new Date()));
    const [duration, setDuration] = useState<Dayjs>(training?.DURATION ? dayjs(training.DURATION) : dayjs(new Date()));
    const [isSelectorTypeOpen, setisSelectorTypeOpen] = useState(false)
    const [isSelectorModeOpen, setisSelectorModeOpen] = useState(false)
    const [addTraining] = useAddTrainingMutation();
    const [updateTraining] = useUpdateTrainingMutation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(false);
    const [imagePreview, setImagePreview] = useState<string | null>(training?.IMAGE ?? null);
    // Edit only: the user cleared the existing cover (and picked nothing new).
    const [imageRemoved, setImageRemoved] = useState(false);
    const [touched, setTouched] = useState<Partial<Record<'TITLE' | 'DISTANCE' | 'CLIMB' | 'START_POINT' | 'CITY' | 'MODE', boolean>>>({});

    const [activity, setActivity] = useState<ActivityFormState>({
        TITLE: training?.TITLE ?? '',
        ADMIN: userID,
        TYPE: (training?.TYPE as ActivityFormState['TYPE']) ?? 'Run',
        MODE: training?.MODE ?? '',
        DISTANCE: training?.DISTANCE != null ? String(training.DISTANCE) : '',
        CLIMB: training?.CLIMB != null ? String(training.CLIMB) : '',
        START_TIME: null,
        DURATION: null,
        START_POINT: training?.START_POINT ?? '',
        CITY: training?.CITY ?? '',
        IMAGE: training?.IMAGE ?? null,
    });

    useEffect(() => () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    }, [imagePreview]);

    const fieldConfig = SPORT_FIELD_CONFIG[activity.TYPE as (typeof SPORT_TYPES)[number]] ?? SPORT_FIELD_CONFIG.Autre;

    const isPositiveNumber = (value: string) => value.trim() !== '' && !isNaN(Number(value)) && Number(value) >= 0;

    const errors = {
      TITLE: activity.TITLE.trim().length === 0,
      MODE: activity.MODE.trim().length === 0,
      DISTANCE: !isPositiveNumber(activity.DISTANCE),
      CLIMB: fieldConfig.showClimb && !isPositiveNumber(activity.CLIMB),
      START_POINT: activity.START_POINT.trim().length === 0,
      CITY: activity.CITY.trim().length === 0,
    };
    const isValid = !Object.values(errors).some(Boolean);

    const markTouched = (field: keyof typeof touched) => () => setTouched((prev) => ({ ...prev, [field]: true }));

    const handleSaveTraining = async () => {
      setTouched({ TITLE: true, MODE: true, DISTANCE: true, CLIMB: true, START_POINT: true, CITY: true });
      if (!isValid || clubId === '') return;

      const startTimeString = startTime.toISOString().slice(0, 19).replace('T', ' ');
      const durationString = duration.toISOString().slice(0, 19).replace('T', ' ');

      const formData = new FormData();
      formData.append('CLUB_ID', String(clubId));
      formData.append('TITLE', activity.TITLE);
      formData.append('TYPE', activity.TYPE);
      formData.append('MODE', activity.MODE);
      formData.append('DISTANCE', activity.DISTANCE);
      formData.append('CLIMB', fieldConfig.showClimb ? activity.CLIMB : '0');
      formData.append('START_TIME', startTimeString);
      formData.append('DURATION', durationString);
      formData.append('START_POINT', activity.START_POINT);
      formData.append('CITY', activity.CITY);
      // A freshly picked file is uploaded; clearing the cover on an edit sends
      // REMOVE_IMAGE; leaving it untouched keeps the existing cover, and a
      // photo-less outing gets the card's generated cover.
      if (activity.IMAGE instanceof File) {
        formData.append('image', activity.IMAGE);
      } else if (isEdit && imageRemoved) {
        formData.append('REMOVE_IMAGE', 'true');
      }

      setError(false);
      setIsSubmitting(true);
      try {
        if (training) {
          await updateTraining({ trainingId: training.ID, formData }).unwrap();
        } else {
          await addTraining(formData).unwrap();
        }
        onClose();
      } catch (error) {
        console.error('error', error);
        setError(true);
      } finally {
        setIsSubmitting(false);
      }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => {
      const { name, value } = e.target;
      setActivity((prev) => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        setActivity((prev) => ({ ...prev, IMAGE: file }));
        setImagePreview(URL.createObjectURL(file));
        setImageRemoved(false);
      }
    };

    const handleAddImageClick = () => {
      trainingImageInputRef.current?.click();
    };

    const handleRemoveImage = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (imagePreview?.startsWith('blob:')) URL.revokeObjectURL(imagePreview);
      setImagePreview(null);
      setActivity((prev) => ({ ...prev, IMAGE: null }));
      setImageRemoved(true);
      if (trainingImageInputRef.current) trainingImageInputRef.current.value = '';
    };

    const handleDateChange = (date: Dayjs | null) => {
      if (!date) return;
      const updatedStartTime = dayjs(startTime)
        .date(date.date())
        .month(date.month())
        .year(date.year());

      setStartTime(updatedStartTime);
    };

    const handleHourChange = (time: Dayjs | null) => {
      if (!time) return;
      const updatedStartTime = dayjs(startTime)
        .hour(time.hour())
        .minute(time.minute());

      setStartTime(updatedStartTime);
    };

    const handleEstimatedTimeChange = (time: Dayjs | null) => {
      if (!time) return;
      setDuration(time);
    };

    return (
        <Dialog
          open
          onClose={() => { if (!isSubmitting) onClose(); }}
          fullWidth
          maxWidth="sm"
          fullScreen={fullScreen}
          PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 3 } }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
            <Typography variant="h6" component="span" fontWeight={600}>
              {isEdit ? t('createActivity.editTitle') : t('createActivity.title')}
            </Typography>
            <IconButton onClick={onClose} disabled={isSubmitting} size="small" aria-label={t('common.cancel')}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {error && (
              <Box mb={2}>
                <Alert severity="error">{t('common.genericError')}</Alert>
              </Box>
            )}

            {!isEdit && managerClubs.length > 1 && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="activity-club-label">{t('createActivity.club')}</InputLabel>
                <Select
                  labelId="activity-club-label"
                  label={t('createActivity.club')}
                  value={clubId === '' ? '' : String(clubId)}
                  onChange={(e) => setClubId(Number(e.target.value))}
                >
                  {managerClubs.map((club) => (
                    <MenuItem key={club.CLUB_ID} value={String(club.CLUB_ID)}>{club.NAME}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Box
              onClick={handleAddImageClick}
              role="button"
              tabIndex={0}
              sx={{
                mb: 3,
                height: 160,
                borderRadius: 2,
                border: '1px dashed',
                borderColor: 'divider',
                cursor: 'pointer',
                overflow: 'hidden',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'action.hover',
                backgroundImage: imagePreview ? `url(${imagePreview})` : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                transition: 'border-color 0.2s ease',
                '&:hover': { borderColor: 'primary.main' },
              }}
            >
              {!imagePreview && (
                <Stack alignItems="center" spacing={0.5} sx={{ color: 'text.secondary' }}>
                  <AddPhotoAlternateIcon fontSize="large" />
                  <Typography variant="body2">{t('createActivity.addImage')}</Typography>
                </Stack>
              )}
              {imagePreview && (
                <>
                  <IconButton
                    size="small"
                    aria-label={t('createActivity.removeImage')}
                    onClick={handleRemoveImage}
                    sx={{
                      position: 'absolute',
                      top: 6,
                      right: 6,
                      zIndex: 1,
                      bgcolor: 'rgba(0,0,0,0.55)',
                      color: 'common.white',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      pointerEvents: 'none',
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'flex-end',
                      p: 1,
                      background: 'linear-gradient(to top, rgba(0,0,0,0.5), transparent 55%)',
                    }}
                  >
                    <Typography variant="caption" sx={{ color: 'common.white', fontWeight: 500 }}>
                      {t('createActivity.changeImage')}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
            <input
              type="file"
              accept="image/*"
              name="IMAGE"
              style={{ display: 'none' }}
              ref={trainingImageInputRef}
              onChange={handleImageChange}
            />

            <TextField
              label={t('createActivity.activityTitle')}
              name="TITLE"
              value={activity.TITLE}
              onChange={handleInputChange}
              onBlur={markTouched('TITLE')}
              error={Boolean(touched.TITLE && errors.TITLE)}
              helperText={touched.TITLE && errors.TITLE ? t('common.requiredField') : ' '}
              fullWidth
              required
              sx={{ mb: 1 }}
            />

            <SectionLabel icon={<RouteIcon fontSize="small" color="action" />}>{t('createActivity.sectionRoute')}</SectionLabel>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
              <FormControl required fullWidth>
                <InputLabel id="activity-type-label">{t('createActivity.type')}</InputLabel>

                <Select
                  labelId="activity-type-label"
                  label={t('createActivity.type')}
                  name='TYPE'
                  value={activity.TYPE}
                  onChange={handleInputChange}
                  fullWidth
                  open={isSelectorTypeOpen}
                  onClick={() => setisSelectorTypeOpen(!isSelectorTypeOpen)}

                >
                  {SPORT_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>{sportTypeLabel(type)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl required fullWidth error={Boolean(touched.MODE && errors.MODE)}>
                  <InputLabel id="activity-mode-label">{t('createActivity.mode')}</InputLabel>

                  <Select
                    labelId="activity-mode-label"
                    label={t('createActivity.mode')}
                    name='MODE'
                    value={activity.MODE}
                    onChange={handleInputChange}
                    onBlur={markTouched('MODE')}
                    fullWidth
                    open={isSelectorModeOpen}
                    onClick={() => setisSelectorModeOpen(!isSelectorModeOpen)}

                  >
                    <MenuItem value="blablarun">{t('createActivity.modeOptions.blablarun')}</MenuItem>
                    <MenuItem value="avec pause">{t('createActivity.modeOptions.withBreak')}</MenuItem>
                    <MenuItem value="on fonce">{t('createActivity.modeOptions.fullSend')}</MenuItem>
                  </Select>
                  <FormHelperText>
                    {touched.MODE && errors.MODE ? t('common.requiredField') : ' '}
                  </FormHelperText>
                </FormControl>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 3, '& > *': { flex: 1 } }}>
              <TextField
                name="DISTANCE"
                label={t('createActivity.distance')}
                type="number"
                value={activity.DISTANCE}
                onChange={handleInputChange}
                onBlur={markTouched('DISTANCE')}
                error={Boolean(touched.DISTANCE && errors.DISTANCE)}
                helperText={touched.DISTANCE && errors.DISTANCE ? t('common.requiredField') : ' '}
                fullWidth
                required
                InputProps={{
                  inputProps: {min: 0, step:"0.1"},
                  endAdornment: <InputAdornment position="end">{fieldConfig.distanceUnit}</InputAdornment>,
                }}
              />
              {fieldConfig.showClimb && (
                <TextField
                  name="CLIMB"
                  label={t('createActivity.climb')}
                  type="number"
                  value={activity.CLIMB}
                  onChange={handleInputChange}
                  onBlur={markTouched('CLIMB')}
                  error={Boolean(touched.CLIMB && errors.CLIMB)}
                  helperText={touched.CLIMB && errors.CLIMB ? t('common.requiredField') : ' '}
                  fullWidth
                  required
                  InputProps={{
                    inputProps: {min: 0, step:"0.1"},
                    endAdornment: <InputAdornment position="end">m</InputAdornment>,
                  }}
                />
              )}
            </Stack>

            <SectionLabel icon={<PlaceIcon fontSize="small" color="action" />}>{t('createActivity.sectionLocation')}</SectionLabel>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 3 }}>
              <LocationAutocomplete
                label={t('createActivity.startPoint')}
                required
                value={activity.START_POINT}
                onTextChange={(text) => setActivity((prev) => ({ ...prev, START_POINT: text }))}
                onPick={(s) =>
                  setActivity((prev) => ({
                    ...prev,
                    START_POINT: s.name,
                    // Only auto-fill the city when we got one and the user hasn't typed their own.
                    CITY: s.city && !prev.CITY.trim() ? s.city : prev.CITY,
                  }))
                }
                onBlur={markTouched('START_POINT')}
                error={Boolean(touched.START_POINT && errors.START_POINT)}
                helperText={touched.START_POINT && errors.START_POINT ? t('common.requiredField') : ' '}
              />
              <TextField
                name="CITY"
                label={t('createActivity.city')}
                required
                value={activity.CITY}
                onChange={handleInputChange}
                onBlur={markTouched('CITY')}
                error={Boolean(touched.CITY && errors.CITY)}
                helperText={touched.CITY && errors.CITY ? t('common.requiredField') : ' '}
                fullWidth
              />
            </Stack>

            <SectionLabel icon={<EventIcon fontSize="small" color="action" />}>{t('createActivity.sectionSchedule')}</SectionLabel>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language} localeText={pickersLocaleText}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <DatePicker
                  label={t('createActivity.date')}
                  format="DD-MM-YYYY"
                  value={startTime}
                  onChange={(date: Dayjs | null) => {handleDateChange(date)}}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <TimePicker
                  ampm={false}
                  value={startTime}
                  onChange={(time: Dayjs | null) => {handleHourChange(time)}}
                  label={t('createActivity.hour')}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <TimePicker
                  ampm={false}
                  value={duration}
                  onChange={(time: Dayjs | null) => {handleEstimatedTimeChange(time)}}
                  label={t('createActivity.duration')}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Stack>
            </LocalizationProvider>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} color="primary" disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSaveTraining}
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            {isEdit ? t('common.save') : t('common.add')}
          </Button>
        </DialogActions>

      </Dialog>
    );
}

export default CreateActivity;
